import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    TextField,
    Grid,
    IconButton,
    CircularProgress,
    Button,
    ButtonGroup,
} from '@mui/material';
import { RiCloseLine, RiImageLine, RiVideoLine, RiMenuLine, RiSearchLine } from '@remixicon/react';
import axios from 'axios';
import { setBackground, saveToHistory } from '../../redux/slices/canvasSlice';
import { fetchBackgroundTemplates, setSelectedCategory, setSearchQuery } from '../../redux/slices/assetsSlice';

const PRESIGNED_URL_API = 'https://us-central1-digislidesapp.cloudfunctions.net/s3GeneratePresignedUrl';

/**
 * Extract S3 key from a full S3 URL
 */
const extractS3Key = (url) => {
    try {
        const urlObj = new URL(url);
        // Check if it's an S3 URL
        if (urlObj.hostname.includes('s3') || urlObj.hostname.includes('amazonaws.com')) {
            return urlObj.pathname.substring(1);
        }
        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Generate presigned URL for S3 URLs, or return original URL if not S3
 */
const getAccessibleUrl = async (url) => {
    const s3Key = extractS3Key(url);
    if (!s3Key) {
        // Not an S3 URL, return as-is (might be CDN or public URL)
        return url;
    }

    try {
        const response = await axios.post(
            PRESIGNED_URL_API,
            {
                s3Key: s3Key,
                operationType: 'read',
                expiresIn: 3600,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        const presignedUrl = response.data?.url || response.data?.presignedUrl || response.data;
        return presignedUrl || url;
    } catch (error) {
        console.error('Error generating presigned URL:', error);
        // Fallback to original URL
        return url;
    }
};

/**
 * Background Modal - Opens beside RightSidebar
 * 
 * Two tabs:
 * 1. Colors - Color picker for background color
 * 2. Backgrounds - Image/Video backgrounds from API
 */
const BackgroundModal = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const { background } = useSelector((state) => state.canvas);
    const { 
        backgroundTemplates, 
        backgroundTemplatesLoading,
        selectedCategory,
        searchQuery
    } = useSelector((state) => state.assets);
    const [activeTab, setActiveTab] = useState(0);
    const [filterType, setFilterType] = useState('all'); // 'all', 'image', 'video', 'menu'

    // Helper to extract hex from rgba or return hex
    const getHexFromColor = (color) => {
        if (color && color.startsWith('rgba')) {
            const matches = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
            if (matches) {
                const r = parseInt(matches[1]).toString(16).padStart(2, '0');
                const g = parseInt(matches[2]).toString(16).padStart(2, '0');
                const b = parseInt(matches[3]).toString(16).padStart(2, '0');
                return `#${r}${g}${b}`;
            }
        }
        return color || '#FFFFFF';
    };

    const [selectedColor, setSelectedColor] = useState(
        background.type === 'color' ? getHexFromColor(background.value) : '#FFFFFF'
    );

    // Update selectedColor when background changes
    useEffect(() => {
        if (background.type === 'color') {
            setSelectedColor(getHexFromColor(background.value));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [background.type, background.value]);

    // Fetch background templates when modal opens
    useEffect(() => {
        if (open && activeTab === 1) {
            dispatch(fetchBackgroundTemplates());
        }
    }, [open, activeTab, dispatch]);

    const presetColors = [
        '#FFFFFF',
        '#F5F5F5',
        '#E0E0E0',
        '#000000',
        '#C61F2F',
        '#E42436',
        '#6B22D5',
        '#5B1DB7',
        '#3B82F6',
        '#10B981',
        '#F59E0B',
        '#EF4444',
        '#78CE75', // Green from image
    ];

    const handleColorSelect = (color) => {
        dispatch(saveToHistory());
        const currentOpacity = background.opacity !== undefined ? background.opacity : 100;
        dispatch(setBackground({ type: 'color', value: color, opacity: currentOpacity }));
        setSelectedColor(color);
    };

    const handleBackgroundSelect = async (template) => {
        dispatch(saveToHistory());

        // Determine the URL to use
        let urlToUse = template.url;
        if (template.type === 'menu') {
            urlToUse = template.blank || template.finished || template.url;
        } else if (template.type === 'video') {
            urlToUse = template.url; // Video URL
        }

        console.log('Selecting background:', { template, urlToUse });

        // Generate presigned URL if it's an S3 URL
        const accessibleUrl = await getAccessibleUrl(urlToUse);

        console.log('Background URL (after presigned):', accessibleUrl);

        const backgroundPayload = {
            type: template.type === 'menu' ? 'image' : template.type, // Menu templates use image type
            value: accessibleUrl,
            opacity: background.opacity !== undefined ? background.opacity : 100,
        };

        console.log('Setting background:', backgroundPayload);

        dispatch(setBackground(backgroundPayload));
        onClose();
    };

    // Get unique cuisine names from templates
    const cuisineNames = useMemo(() => {
        const cuisines = new Set();
        backgroundTemplates.forEach((template) => {
            if (template.cuisineName) {
                cuisines.add(template.cuisineName);
            }
        });
        return Array.from(cuisines).sort();
    }, [backgroundTemplates]);

    // Filter templates based on selected filter, search query, and cuisine category
    const filteredTemplates = useMemo(() => {
        let filtered = backgroundTemplates;

        // Filter by type (all, image, video, menu)
        if (filterType !== 'all') {
            filtered = filtered.filter((template) => template.type === filterType);
        }

        // Filter by cuisine category
        if (selectedCategory && selectedCategory !== 'all') {
            filtered = filtered.filter((template) => 
                template.cuisineName && template.cuisineName.toLowerCase() === selectedCategory.toLowerCase()
            );
        }

        // Filter by search query
        if (searchQuery && searchQuery.trim() !== '') {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter((template) => {
                const nameMatch = template.name?.toLowerCase().includes(query);
                const cuisineMatch = template.cuisineName?.toLowerCase().includes(query);
                return nameMatch || cuisineMatch;
            });
        }

        return filtered;
    }, [backgroundTemplates, filterType, selectedCategory, searchQuery]);

    if (!open) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                right: 320, // Position beside RightSidebar (width: 320)
                top: 64, // Below TopHeaderBar (height: 64)
                width: 300,
                height: 'calc(100vh - 64px)',
                backgroundColor: 'white',
                borderLeft: '1px solid #E0E0E0',
                boxShadow: '-4px 0 12px rgba(0,0,0,0.1)',
                zIndex: 999,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'calc(100vh - 200px)',
                borderRadius: 1,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    borderBottom: '1px solid #E0E0E0',
                }}
            >
                <Typography variant="h6" fontWeight={600}>
                    Background
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <RiCloseLine size={20} />
                </IconButton>
            </Box>

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                    borderBottom: '1px solid #E0E0E0',
                    '& .MuiTabs-indicator': {
                        display: 'none',
                    },
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '14px',
                        minHeight: 'auto',
                        padding: '12px 24px',
                        '&.Mui-selected': {
                            color: '#000',
                            fontWeight: 600,
                            borderBottom: '2px solid #000',
                        },
                    },
                }}
            >
                <Tab label="Colors" />
                <Tab label="Backgrounds" />
            </Tabs>

            {/* Tab Content */}
            <Box sx={{ flex: 1, overflow: 'auto', padding: '16px' }}>
                {/* Colors Tab */}
                {activeTab === 0 && (
                    <Box>
                        {/* Color Picker */}
                        <Box sx={{ marginBottom: '50px' }}>
                            <TextField
                                type="color"
                                value={selectedColor}
                                onChange={(e) => {
                                    setSelectedColor(e.target.value);
                                    handleColorSelect(e.target.value);
                                }}
                                sx={{
                                    width: '100%',
                                    height: '60px',
                                    '& input': {
                                        height: '60px',
                                        cursor: 'pointer',
                                    },
                                }}
                            />
                        </Box>

                        {/* Hex Input */}
                        <Box sx={{ marginBottom: '24px' }}>
                            <TextField
                                label="Hex Color"
                                value={selectedColor.replace('#', '').toUpperCase()}
                                onChange={(e) => {
                                    const value = e.target.value.replace('#', '');
                                    if (value.length === 6) {
                                        const color = `#${value}`;
                                        setSelectedColor(color);
                                        handleColorSelect(color);
                                    }
                                }}
                                size="small"
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& input': {
                                            textTransform: 'uppercase',
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Preset Colors */}
                        <Typography variant="subtitle2" sx={{ marginBottom: '12px' }}>
                            Preset Colors
                        </Typography>
                        <Grid container spacing={1}>
                            {presetColors.map((color) => (
                                <Grid item key={color}>
                                    <Box
                                        onClick={() => handleColorSelect(color)}
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            backgroundColor: color,
                                            border:
                                                selectedColor === color
                                                    ? '3px solid #6B22D5'
                                                    : '1px solid #E0E0E0',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                transform: 'scale(1.1)',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                            },
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* Backgrounds Tab */}
                {activeTab === 1 && (
                    <Box>
                        {/* Search Input */}
                        <Box sx={{ marginBottom: '16px' }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search backgrounds..."
                                value={searchQuery || ''}
                                onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                                InputProps={{
                                    startAdornment: (
                                        <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                                            <RiSearchLine size={18} color="#666" />
                                        </Box>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        backgroundColor: '#F5F5F5',
                                        '&:hover': {
                                            backgroundColor: '#EEEEEE',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: '#FFFFFF',
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Cuisine Category Filter */}
                        {cuisineNames.length > 0 && (
                            <Box sx={{ marginBottom: '16px' }}>
                                <Typography variant="caption" sx={{ marginBottom: '8px', display: 'block', color: '#666', fontWeight: 500 }}>
                                    Categories
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    <Button
                                        size="small"
                                        variant={selectedCategory === 'all' || !selectedCategory ? 'contained' : 'outlined'}
                                        onClick={() => dispatch(setSelectedCategory('all'))}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '12px',
                                            padding: '4px 12px',
                                            minWidth: 'auto',
                                            ...(selectedCategory === 'all' || !selectedCategory ? {
                                                backgroundColor: '#6B22D5',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#5B1DB7',
                                                },
                                            } : {
                                                borderColor: '#E0E0E0',
                                                color: '#666',
                                            }),
                                        }}
                                    >
                                        All
                                    </Button>
                                    {cuisineNames.map((cuisine) => (
                                        <Button
                                            key={cuisine}
                                            size="small"
                                            variant={selectedCategory === cuisine ? 'contained' : 'outlined'}
                                            onClick={() => dispatch(setSelectedCategory(cuisine))}
                                            sx={{
                                                textTransform: 'none',
                                                fontSize: '12px',
                                                padding: '4px 12px',
                                                minWidth: 'auto',
                                                ...(selectedCategory === cuisine ? {
                                                    backgroundColor: '#6B22D5',
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: '#5B1DB7',
                                                    },
                                                } : {
                                                    borderColor: '#E0E0E0',
                                                    color: '#666',
                                                }),
                                            }}
                                        >
                                            {cuisine}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Filter Buttons */}
                        <Box sx={{ marginBottom: '16px' }}>
                            <ButtonGroup
                                variant="outlined"
                                size="small"
                                fullWidth
                                sx={{
                                    '& .MuiButton-root': {
                                        textTransform: 'none',
                                        fontSize: '12px',
                                        padding: '6px 12px',
                                        borderColor: '#E0E0E0',
                                        // color: '#666',
                                        '&.Mui-selected': {
                                            backgroundColor: '#6B22D5',
                                            color: 'white',
                                            borderColor: '#6B22D5',
                                            '&:hover': {
                                                backgroundColor: '#5B1DB7',
                                                borderColor: '#5B1DB7',
                                            },
                                        },
                                    },
                                }}
                            >
                                <Button
                                    onClick={() => setFilterType('all')}
                                    variant={filterType === 'all' ? 'contained' : 'outlined'}
                                    sx={{
                                        ...(filterType === 'all' && {
                                            backgroundColor: '#6B22D5',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: '#5B1DB7',
                                            },
                                        }),
                                    }}
                                >
                                    All
                                </Button>
                                {/* <Button
                                    onClick={() => setFilterType('menu')}
                                    variant={filterType === 'menu' ? 'contained' : 'outlined'}
                                    startIcon={<RiMenuLine size={16} />}
                                    sx={{
                                        ...(filterType === 'menu' && {
                                            backgroundColor: '#6B22D5',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: '#5B1DB7',
                                            },
                                        }),
                                    }}
                                >
                                    Menus
                                </Button> */}
                                <Button
                                    onClick={() => setFilterType('image')}
                                    variant={filterType === 'image' ? 'contained' : 'outlined'}
                                    startIcon={<RiImageLine size={16} />}
                                    sx={{
                                        ...(filterType === 'image' && {
                                            backgroundColor: '#6B22D5',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: '#5B1DB7',
                                            },
                                        }),
                                    }}
                                >
                                    Images
                                </Button>
                                <Button
                                    onClick={() => setFilterType('video')}
                                    variant={filterType === 'video' ? 'contained' : 'outlined'}
                                    startIcon={<RiVideoLine size={16} />}
                                    sx={{
                                        ...(filterType === 'video' && {
                                            backgroundColor: '#6B22D5',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: '#5B1DB7',
                                            },
                                        }),
                                    }}
                                >
                                    Videos
                                </Button>
                            </ButtonGroup>
                        </Box>

                        {backgroundTemplatesLoading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '200px',
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : filteredTemplates.length === 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '200px',
                                    color: '#666',
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    No {filterType !== 'all' ? filterType : ''} backgrounds available
                                </Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                {filteredTemplates.map((template) => {
                                    // Determine preview image/video source
                                    let previewSrc = template.url;
                                    if (template.type === 'menu' || template.type === 'image') {
                                        previewSrc = template.blank || template.finished || template.url;
                                    } else if (template.type === 'video') {
                                        previewSrc = template.thumbnail || template.url;
                                    }

                                    const isSelected =
                                        background.type === (template.type === 'menu' ? 'image' : template.type) &&
                                        background.value === (template.type === 'menu' ? template.blank || template.finished : template.url);

                                    return (
                                        <Grid item xs={6} key={template.id}>
                                            <Box
                                                onClick={() => handleBackgroundSelect(template)}
                                                sx={{
                                                    position: 'relative',
                                                    width: '100%',
                                                    paddingTop: '56.25%', // 16:9 aspect ratio
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                    border: isSelected
                                                        ? '3px solid #6B22D5'
                                                        : '1px solid #E0E0E0',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        transform: 'scale(1.02)',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                    },
                                                }}
                                            >
                                                {/* Preview Image/Thumbnail */}
                                                {(template.type === 'image' || template.type === 'menu') ? (
                                                    <Box
                                                        component="img"
                                                        src={previewSrc}
                                                        alt={template.name}
                                                        onError={(e) => {
                                                            // Fallback if image fails to load
                                                            e.target.style.display = 'none';
                                                        }}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                ) : template.type === 'video' ? (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                        }}
                                                    >
                                                        {/* Video Thumbnail */}
                                                        {template.thumbnail ? (
                                                            <Box
                                                                component="img"
                                                                src={template.thumbnail}
                                                                alt={template.name}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                }}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    backgroundColor: '#E3F2FD',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <RiVideoLine size={32} color="#1976D2" />
                                                            </Box>
                                                        )}
                                                        {/* Play Icon Overlay */}
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                left: '50%',
                                                                transform: 'translate(-50%, -50%)',
                                                                width: '48px',
                                                                height: '48px',
                                                                borderRadius: '50%',
                                                                backgroundColor: 'rgba(0,0,0,0.6)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: 'white',
                                                            }}
                                                        >
                                                            <RiVideoLine size={24} />
                                                        </Box>
                                                    </Box>
                                                ) : null}

                                                {/* Type Badge */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '8px',
                                                        right: '8px',
                                                        backgroundColor: 'rgba(0,0,0,0.7)',
                                                        color: 'white',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '10px',
                                                        fontWeight: 600,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        textTransform: 'capitalize',
                                                    }}
                                                >
                                                    {template.type === 'image' && <RiImageLine size={12} />}
                                                    {template.type === 'video' && <RiVideoLine size={12} />}
                                                    {template.type === 'menu' && <RiMenuLine size={12} />}
                                                    {template.type}
                                                </Box>
                                            </Box>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: 'block',
                                                    marginTop: '4px',
                                                    textAlign: 'center',
                                                    color: '#666',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {template.name}
                                            </Typography>
                                            {template.cuisineName && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: 'block',
                                                        marginTop: '2px',
                                                        textAlign: 'center',
                                                        color: '#999',
                                                        fontSize: '10px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {template.cuisineName}
                                                </Typography>
                                            )}
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default BackgroundModal;

